import workspace from "../workspace";
import {v4 as uuidv4} from "uuid";
import store from "../../../../../../store";
import {addResource, changeResource} from "../../../../../../actions/sqftResourceFunctions";
import {getEdgeFeatures} from "../../../../helpers/shapeHelper";
import {getBacksplashArea, getEdgeArea, getEdgeLength} from "../../../../helpers/sqftHelper";
import {RESOURCE_ATTACHED_TO_TYPE, RESOURCE_TYPE} from "../../../../constants";
import {setSQFTPopup} from "../../../../../../actions/sqftMenuFunctions";
import {getQtCatName} from "../../../../helpers/sqftQuotesHelper";
import sqftConfig from "../../store/sqftConfig";

/**
 * Prepare and attach resourceType(edge, backsplash, bumpout, mitered) to the line or lineCorner
 * @param {*} seg
 * @param {*} segCorner
 * @param {*} resourceType
 * @param {number} featureId
 * @param {string} partId
 * @param {*} featureName
 * @param height
 * @param {string} resourceId
 * @param qtCatName
 * @param {boolean} addResourceToStore
 * @param {boolean} shouldShowEyeBall
 */
export const attachResource = (
  seg,
  segCorner,
  resourceType,
  featureId,
  partId,
  featureName,
  height = 0,
  resourceId = null,
  qtCatName = null,
  addResourceToStore = true,
  shouldShowEyeBall = true,
) => {
  const attachedToHovered = segCorner || seg;
  const attachedToExisting = workspace.getAttachedTo(attachedToHovered);
  const attachedToResource = prepareResource(seg, segCorner, resourceType, featureId, partId, featureName, resourceId, qtCatName, height, shouldShowEyeBall);

  attachResourceToLine(seg, segCorner, resourceType, featureId, partId, featureName, height, shouldShowEyeBall);

  if (addResourceToStore) {
    createAttachedResourceInStore(attachedToExisting, attachedToResource);
  }

  closeSQFTPopup();
}

const createAttachedResourceInStore = (shouldChangeExisting, attachedToResource) => {
  const action = shouldChangeExisting ? changeResource : addResource;
  store.dispatch(action(RESOURCE_TYPE.attachedToLine, attachedToResource));
}

const attachResourceToLine = (seg, segCorner, resourceType, featureId, partId, featureName, height) => {
  let historyCallBack = () => {};
  let newSection;

  const resourceActionMap = {
    'edge': (segCorner) ? segCorner.addEdgeToCornerLine.bind(this, featureName, featureId) : seg.addEdgeToLine.bind(this, featureName, featureId),
    'backsplash': (segCorner) ? segCorner.addBacksplashToCornerLine.bind(this, featureName, resourceType, featureId, height) : seg.addBacksplashToLine.bind(this, featureName, resourceType, featureId, height),
    [RESOURCE_ATTACHED_TO_TYPE.bumpout]: () => {
      if (seg && !seg.isWithBumpout()) seg.addBumpoutToLine();
    },
    [RESOURCE_ATTACHED_TO_TYPE.mitered]: () => {
      if (seg && !seg.isWithMitered()) {
        newSection = seg.addMiteredToLine();
        if (newSection) workspace.miteredController.addSection(newSection);
      }
      if (segCorner && !segCorner.isWithMitered()) {
        newSection = segCorner.addMiteredToCornerLine();
        if (newSection) workspace.miteredController.addSection(newSection);
      }
    },
    [RESOURCE_ATTACHED_TO_TYPE.underlayment]: () => {
      if (seg && !seg.isWithMitered()) {
        newSection = seg.addUnderlaymentToLine();
        if (newSection) workspace.underlaymentController.addSection(newSection);
      }
    },
    [RESOURCE_ATTACHED_TO_TYPE.crown_molding]: () => seg.addCrownMoldingToLine(featureName, featureId),
    [RESOURCE_ATTACHED_TO_TYPE.end_cap]: () => seg.addEndCapToLine(featureName, featureId),
  };

  if (resourceActionMap[resourceType]) {
    historyCallBack = resourceActionMap[resourceType];
  }

  historyCallBack();
}

export const closeSQFTPopup = () => {
  const state = store.getState();
  const isSQFTPopupOpened = state.sqftMenuState.sqftPopup.isOpened;

  if (isSQFTPopupOpened) {
    store.dispatch(setSQFTPopup({
      title: workspace.intl.formatMessage({ id: "sqft.popup.title.systemMessage" }),
      content: '',
      isOpened: false,
      confirmCallback: () => {},
      confirmText: '',
      cancelCallback: () => {},
      cancelText: ''
    }));
  }
}

const prepareResource = (seg, segCorner, resourceType, featureId, partId, featureName, resourceId, qtCatName, height, shouldShowEyeBall = true) => {
  const attachedToHovered = segCorner || seg;
  const id = resourceId || (attachedToHovered && attachedToHovered.id) || uuidv4();

  let attachedToParentId = null;
  if (attachedToHovered && attachedToHovered.getParent) {
    const parent = attachedToHovered.getParent();
    attachedToParentId = parent ? parent.id : null;
  }

  return {
    id,
    type: resourceType,
    featureId,
    name: featureName,
    attachedToId: attachedToHovered ? attachedToHovered.id : null,
    partId,
    part: segCorner ? 'lineCorner' : 'line',
    attachedToParentId,
    isDisabledInPopup: attachedToHovered ? attachedToHovered.isDisabledInPopup : null,
    qtCatName: qtCatName || getQtCatName(),
    shouldShowEyeBall,
    height,

    export() {
      let length = 0, realLength = 0, lineLength = 0, area = 0;
      const line = workspace.getLineOrCornerLineById(this.attachedToId);

      if (line) {
        switch (resourceType) {
          case RESOURCE_ATTACHED_TO_TYPE.edge: {
            const navItems = getEdgeFeatures();
            const navItem = navItems.find(edge => edge.part.dbFeature.id === this.featureId);
            const feature = navItem ? navItem.part : null;
            const unit = feature ? feature.dbFeature.unit : null;
            length = (unit && unit.toLowerCase() === "each") ? 1 : getEdgeLength(line);
            realLength = getEdgeLength(line);
            lineLength = sqftConfig.measureUnit === "Imperial" ? line.getLineLength() : line.getLineLengthMm();
            area = getEdgeArea(line, lineLength, featureId);
            break;
          }
          case RESOURCE_ATTACHED_TO_TYPE.backsplash:
            length = getBacksplashArea(line.getBacksplashHeight(), line);
            break;
          default:
            length = realLength = getEdgeLength(line);
            break;
        }
        return { ...this, length, realLength, lineLength, area };
      }

      return {};
    }
  };
};

